<template>
    <div
        v-auto-animate class="group font-oceanside"
        :class="isFullWidth ? 'w-full' : 'w-[48%]'"
        :id="`step-options-initials-button-${mainPlacement.code}`"
        :style="{
            scrollMarginTop: `${scrollToOffset}px`,
        }"
    >
        <!-- This button is really a selector of whether we want a stamping or not -->

        <div v-if="hasImage"
            @click="handleOpenInitialsOptionsPanel"
            :class="[
                'h-[128px] xs:h-36 bg-gray-F6F6F6 border border-b-0 cursor-pointer bg-no-repeat w-full',
                !hasErrorState && 'group-hover:border-black group-hover:border-b-0',
                hasErrorState ? 'border-red' : (mainOption?.selected ? 'border-black' : 'border-gray-DFDFDF'),
                currentGroup === 'Character Stamping' && 'bg-top bg-cover'
            ]"
            :style="{
                backgroundImage: `url(&quot;${getImage(mainOption)}&quot;)`,
                backgroundPosition: 'center',
                backgroundSize,
                backgroundRepeat: 'no-repeat',

            }"
        >
        </div>
        <button
            type="button"
            @click="handleOpenInitialsOptionsPanel"
            :class="[
                hasErrorState ? 'border-red' : (mainOption?.selected ? 'border-black' : 'border-gray-DFDFDF'),
                !hasImage && 'border-t',
                'border-b border-l border-r h-12 w-full flex items-center justify-between font-sans p-4 mb-[32px] text-[16px] tracking-[0.56px]',
                !hasErrorState && 'group-hover:border-black',
                'group-focus:border-l group-focus:border-r group-focus:border-t',
                'group-focus:outline-none group-focus:ring-1 group-focus:ring-black group-focus:border-black ring-offset-[-2px]'
            ]"
            :data-code="mainOption?.raw?.code"
        >
            <div
                class="flex justify-between items-center w-full text-start leading-4 font-oceanside"
            >
                <div>
                    {{ getFormattedLabel(mainOption?.label) }}
                    <span v-if="basePriceFormatted" class="ml-1">
                        ({{ basePriceFormatted }})
                    </span>
                </div>
                <div class="flex items-center gap-6">
                    <black-white-button
                        v-if="hasAddRemoveButton"
                        class="w-[90px] rounded-full font-semibold"
                        :is-selected="!mainOption?.selected"
                        @click.stop.prevent="handleAddRemoveClick"
                    >
                        {{ (mainOption?.selected) ? $t('REMOVE') : $t('ADD') }}
                    </black-white-button>
                    <button
                        v-if="shouldShowEditButton && mainOption?.selected" @click="handleOpenInitialsOptionsPanel"
                        class="flex items-center gap-2"
                    >
                        <span class="underline">Edit</span>
                        <edit-icon class="w-4 h-4" />
                    </button>
                </div>
            </div>
        </button>

        <slot></slot>
    </div>
</template>

<script>
    import BlackWhiteButton from './BlackWhiteButton.vue';
    import allStores from '../../stores/all';
    import getImage from '../../helpers/getimage';
    import EditIcon from '../EditIcon.vue';

    export default {
        mixins: [allStores],

        components: {
            BlackWhiteButton,
            EditIcon,
        },

        props: {
            hasLockIcon: {
                type: Boolean,
                default: false,
            },
            hasCheckmarkOnSelected: {
                type: Boolean,
                default: false,
            },
            mainPlacement: Object,
            allComponents: Array,
            option: Object,
            index: Number,
            scrollToTimeout: {
                type: Number,
                default: 400,
            },
            scrollToOffset: {
                type: Number,
                default: 0,
            },
            hasImage: {
                type: Boolean,
                default: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
            hasErrorState: {
                type: Boolean,
                default: false,
            },
            isFullWidth: {
                type: Boolean,
                default: true,
            },
            hasZoomIn: {
                type: Boolean,
                default: false,
            },
            zoomImageSizePercent: {
                type: Number,
                default: 100,
            },
            hasAddRemoveButton: {
                type: Boolean,
                default: false,
            },
            usePaletteColorSelection: {
                type: Boolean,
                default: false,
            },
            shouldScrollToClickedButton: {
                type: Boolean,
                default: true,
            },
            removeSelectedOnAddRemoveClick: {
                type: Boolean,
                default: false,
            },
            navigateOnClick: {
                type: Boolean,
                default: true,
            },
            shouldShowEditButton: {
                type: Boolean,
                default: false,
            },
            backgroundSize: {
                type: String,
                default: 'contain',
            },
        },

        emits: ['select', 'unselect'],

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            isComplete() {
                return this.selectionsMade?.includes('Character Stamping');
            },

            panelsInErrorState() {
                return this.customizerStore.panelsInErrorState;
            },

            basePriceFormatted() {
                if (this.mainOption == null) {
                    return null;
                }

                if (!this.mainOption.raw) {
                    return null;
                }

                return this.customizerStore.priceFormatted(
                    this.customizerStore.getPrice(this.viewController, this.mainOption.raw, 'basePrice'),
                    true,
                );
            },

            addonPriceFormatted() {
                if (!this.mainOption.raw) {
                    return null;
                }

                return this.customizerStore.priceFormatted(
                    this.customizerStore.getPrice(this.viewController, this.mainOption.raw, 'addonPrice'),
                    true,
                );
            },

            /**
             * Kind of fake-ish option that represents whether character stamping is enabled at all.
             */
            mainOption() {
                const selected = this.currentComponent;

                const component = this.mainPlacement?.components?.find((x) => !x.placeholder);

                if (!component) {
                    return null;
                }

                return {
                    selected: !selected.placeholder,
                    label: 'Initials',
                    value: component.code,
                    raw: {
                        ...component,
                        custom: {
                            ...(component.custom || {}),
                            ...{
                                'v-thumbnail-asset': 'PutterFace-InitialsSelected',
                            },
                        },
                    },
                };
            },

            currentComponent() {
                return this.viewController.selectedAtPlacement(this.mainPlacement.code);
            },

            selectionsMade() {
                return this.customizerStore.selectionsMade;
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },

            groupPreviewImage() {
                return this.mainPlacement?.custom['v-group-preview'];
            },

            isInitialsActive() {
                const current = this.currentComponent;
                if (!current || current.placeholder) {
                    return false;
                }

                // Handle stamps: check if the placement is 'Stamp' and it's not the blank component
                if (this.mainPlacement?.code === 'Stamp') {
                    return current.code !== 'Stamp-Stamp-Blank';
                }

                // Handle initials: check for initials-style
                return current.custom?.['v-order-2-item'] === 'initials-style';
            },

        },

        methods: {
            setSelectedGroup(group) {
                this.customizerStore.setCurrentGroup(group);
            },

            handleOpenNestedPanel(option) {
                this.appStore.openNestedPanel('zoomIn', {
                    option,
                    mainPlacement: this.mainPlacement,
                    imageSizePercent: this.zoomImageSizePercent,
                });
            },

            handleOpenInitialsOptionsPanel() {
                const hasSelectedStyle = this.selectStyle();

                if (!hasSelectedStyle) {
                    return;
                }

                if (!this.navigateOnClick) {
                    return;
                }

                this.appStore.openNestedPanel('charactersInitialsOptions', {
                    allComponents: this.allComponents,
                    isDirty: this.isDirty,
                    mainPlacement: this.mainPlacement,
                    option: this.option,
                    usePaletteColorSelection: this.usePaletteColorSelection,
                });
            },

            handleAddRemoveClick() {
                if (this.removeSelectedOnAddRemoveClick && this.mainOption?.selected) {
                    this.$emit('unselect');
                    return;
                }

                if (this.navigateOnClick) {
                    this.handleOpenInitialsOptionsPanel();
                    return;
                }

                this.selectStyle();
            },

            getDancingStyle(code) {
                return this.customizerStore.getDancingStyle(code);
            },

            getDotsStyle(code) {
                return this.customizerStore.getDotsStyle(code);
            },

            getFormattedLabel(label) {
                if (label === 'No Thanks') {
                    return 'Stock Paintfill';
                }
                return label;
            },

            scrollToClickedButton() {
                setTimeout(() => {
                    const button = document.getElementById(`step-options-initials-button-${this.mainPlacement.code}`);

                    button.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        alignToTop: true,
                        inline: 'end',
                    });
                }, 1000);
            },

            selectStyle() {
                if (this.isInitialsActive) {
                    return true;
                }

                // Find some option to select.
                let toSelect = this.mainPlacement.components.find((x) => !x.placeholder
                    && this.viewController.isAvailable(x)
                    && this.getDancingStyle(x.code) === 'straight'
                    && this.getDotsStyle(x.code) === false);

                if (toSelect == null) {
                    toSelect = this.mainPlacement.components.find((x) => !x.placeholder
                        && this.viewController.isAvailable(x)
                        && this.getDancingStyle(x.code) === 'dancing'
                        && this.getDotsStyle(x.code) === false);
                }

                if (toSelect == null) {
                    // Whatever...
                    toSelect = this.mainPlacement.components.find((x) => !x.placeholder
                        && this.viewController.isAvailable(x));
                }

                if (toSelect) {
                    this.$emit('select', {
                        placement: this.mainPlacement.code,
                        option: toSelect.code,
                    });
                } else {
                    return false;
                }

                if (this.shouldScrollToClickedButton) {
                    this.scrollToClickedButton();
                }

                this.customizerStore.setActiveView('Face');

                return true;
            },

            async unselectStyle() {
                this.$emit('unselect');
            },

            getFlightLinesImage(option) {
                return getImage(this.viewController, option.raw);
            },

            getImage(option) {
                if (option == null) {
                    return '';
                }

                if (option?.code?.includes('FlightLine')) {
                    return this.getFlightLinesImage(option);
                }

                if (this.groupPreviewImage) {
                    return this.groupPreviewImage;
                }

                return getImage(this.viewController, option.raw);
            },

        },
    };
</script>
